import * as pc from 'playcanvas';
import {
    PLAYER1_KEYBOARD,
    PLAYER2_KEYBOARD,
    GAMEPAD_BUTTONS,
    GAMEPAD_AXES,
    STICK_DEADZONE,
    MOVE_SPEED,
    JUMP_FORCE,
    getGamepads,
    isButtonPressed,
    getAxisValue
} from '../config/controls.js';
import { playSound } from './audio.js';
import { screenShake } from './camera.js';
import { constrainToArenaBounds } from './collision.js';
import { getTouchInput, isTouchDevice } from './touch.js';

// Hazard damage cooldown per player (to prevent rapid damage)
const hazardCooldowns = new Map();
const HAZARD_COOLDOWN_TIME = 1.0; // seconds between hazard damage

// Create keyboard handler
export function createKeyboard() {
    return new pc.Keyboard(document.body);
}

// Check if key is pressed for a specific player
function isKeyPressed(keyboard, keys) {
    return keys.some(key => keyboard.isPressed(key));
}

// Check if key was just pressed for a specific player
function wasKeyPressed(keyboard, keys) {
    return keys.some(key => keyboard.wasPressed(key));
}

// Get input for a specific player
export function getPlayerInput(keyboard, playerNumber, dt) {
    const input = {
        moveX: 0,
        moveZ: 0,
        jump: false,
        attack: false,
        dodge: false
    };

    // Get control mapping for this player
    const keyMap = playerNumber === 1 ? PLAYER1_KEYBOARD : PLAYER2_KEYBOARD;
    const gamepadIndex = playerNumber - 1;

    // Keyboard input
    if (keyboard) {
        if (isKeyPressed(keyboard, keyMap.up)) input.moveZ = -MOVE_SPEED;
        if (isKeyPressed(keyboard, keyMap.down)) input.moveZ = MOVE_SPEED;
        if (isKeyPressed(keyboard, keyMap.left)) input.moveX = -MOVE_SPEED;
        if (isKeyPressed(keyboard, keyMap.right)) input.moveX = MOVE_SPEED;
        if (wasKeyPressed(keyboard, keyMap.jump)) input.jump = true;
        if (wasKeyPressed(keyboard, keyMap.attack)) input.attack = true;
        if (wasKeyPressed(keyboard, keyMap.dodge)) input.dodge = true;
    }

    // Gamepad input
    const pads = getGamepads();
    const pad = pads[gamepadIndex];

    if (pad) {
        // Left stick movement
        const leftStickX = getAxisValue(pad, GAMEPAD_AXES.LEFT_STICK_X);
        const leftStickY = getAxisValue(pad, GAMEPAD_AXES.LEFT_STICK_Y);

        if (Math.abs(leftStickX) > 0) input.moveX = leftStickX * MOVE_SPEED;
        if (Math.abs(leftStickY) > 0) input.moveZ = leftStickY * MOVE_SPEED;

        // Buttons (these need state tracking per player)
        if (isButtonPressed(pad, GAMEPAD_BUTTONS.A)) input.jump = true;
        if (isButtonPressed(pad, GAMEPAD_BUTTONS.Y) || isButtonPressed(pad, GAMEPAD_BUTTONS.B)) input.attack = true;
        if (isButtonPressed(pad, GAMEPAD_BUTTONS.R) || isButtonPressed(pad, GAMEPAD_BUTTONS.L)) input.dodge = true;
    }

    // Touch input (Player 1 only on touch devices)
    if (playerNumber === 1 && isTouchDevice()) {
        const touchInput = getTouchInput();

        // Touch movement overrides keyboard/gamepad if active
        if (touchInput.moveX !== 0 || touchInput.moveZ !== 0) {
            input.moveX = touchInput.moveX;
            input.moveZ = touchInput.moveZ;
        }

        // Touch actions combine with other inputs
        if (touchInput.jump) input.jump = true;
        if (touchInput.attack) input.attack = true;
        if (touchInput.dodge) input.dodge = true;
    }

    return input;
}

// Apply input to player entity
export function applyInputToPlayer(player, input, collisionObjects, dt) {
    if (!player || player.health <= 0) return;

    const currentPos = player.getPosition();
    let newX = currentPos.x + input.moveX * dt;
    let newZ = currentPos.z + input.moveZ * dt;

    // Rotate player to face movement direction
    if (input.moveX !== 0 || input.moveZ !== 0) {
        const angle = Math.atan2(-input.moveX, -input.moveZ);
        player.setEulerAngles(0, angle * (180 / Math.PI), 0);
    }

    // Check collision with pillars and platform sides
    for (const obj of collisionObjects) {
        const dx = newX - obj.x;
        const dz = newZ - obj.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        const playerRadius = 0.5;

        if (obj.type === 'pillar') {
            const minDist = obj.radius + playerRadius;
            if (dist < minDist && dist > 0) {
                newX = obj.x + (dx / dist) * minDist;
                newZ = obj.z + (dz / dist) * minDist;
            }
        }

        if (obj.type === 'platform') {
            const platformTop = obj.y;
            const playerBottom = currentPos.y - 0.5;

            if (playerBottom < platformTop) {
                const minDist = obj.radius + playerRadius;
                if (dist < minDist && dist > 0) {
                    newX = obj.x + (dx / dist) * minDist;
                    newZ = obj.z + (dz / dist) * minDist;
                }
            }
        }

        // Handle hazard collision (spikes, crystals, lava, etc.)
        if (obj.type === 'hazard') {
            const minDist = obj.radius + playerRadius;
            if (dist < minDist && dist > 0) {
                // Push player away from hazard
                newX = obj.x + (dx / dist) * minDist;
                newZ = obj.z + (dz / dist) * minDist;

                // Deal damage with cooldown
                const playerId = player._guid || player.name || 'player';
                const hazardKey = `${playerId}-${obj.name || 'hazard'}`;
                const now = Date.now() / 1000;
                const lastHit = hazardCooldowns.get(hazardKey) || 0;

                if (now - lastHit > HAZARD_COOLDOWN_TIME && !player.isInvincible) {
                    hazardCooldowns.set(hazardKey, now);
                    const damage = obj.damage || 10;
                    player.health = Math.max(0, player.health - damage);
                    player.isInvincible = true;
                    player.invincibleTimer = 0.5; // Brief invincibility after hazard
                    playSound('damage');
                    screenShake(0.15);

                    console.log(`Player ${player.playerNumber || 1} hit by ${obj.name}! Took ${damage} damage. Health: ${player.health}`);

                    // Hide player if defeated
                    if (player.health <= 0) {
                        player.enabled = false;
                        console.log(`Player ${player.playerNumber || 1} defeated by ${obj.name}!`);
                    }
                }
            }
        }
    }

    // Keep player inside arena bounds (dynamic based on arena shape)
    const constrained = constrainToArenaBounds(newX, newZ);
    newX = constrained.x;
    newZ = constrained.z;

    // Apply gravity
    player.velocity.y -= 30 * dt;
    let newY = currentPos.y + player.velocity.y * dt;

    // Check platform collision (can stand on top)
    let onPlatform = false;
    let platformHeight = 0;

    for (const obj of collisionObjects) {
        if (obj.type === 'platform') {
            const dx = newX - obj.x;
            const dz = newZ - obj.z;
            const dist = Math.sqrt(dx * dx + dz * dz);

            if (dist < obj.radius - 0.3) {
                const platformTop = obj.y;

                if (currentPos.y >= platformTop + 0.3 && newY <= platformTop + 0.6) {
                    onPlatform = true;
                    platformHeight = platformTop;
                } else if (currentPos.y >= platformTop + 0.5 && currentPos.y < platformTop + 0.8) {
                    onPlatform = true;
                    platformHeight = platformTop;
                }
            }
        }
    }

    // Ground/platform collision
    const groundLevel = onPlatform ? platformHeight + 0.6 : 0.6;

    if (newY <= groundLevel) {
        newY = groundLevel;
        player.velocity.y = 0;
        player.isJumping = false;
    }

    player.setPosition(newX, newY, newZ);

    return { newX, newY, newZ };
}

// Handle jump action
export function handleJump(player, input) {
    if (input.jump && !player.isJumping && !player.jumpPressed) {
        player.velocity.y = JUMP_FORCE;
        player.isJumping = true;
        player.jumpPressed = true;
        playSound('jump');
        return true;
    }
    if (!input.jump) {
        player.jumpPressed = false;
    }
    return false;
}

// Check for gamepad connection and show message
export function checkGamepadConnection(callback) {
    window.addEventListener('gamepadconnected', (e) => {
        console.log(`Gamepad connected: ${e.gamepad.id}`);
        callback(e.gamepad.index);
    });

    window.addEventListener('gamepaddisconnected', (e) => {
        console.log(`Gamepad disconnected: ${e.gamepad.id}`);
    });
}

// Get first connected gamepad (for single player or menu)
export function getFirstGamepad() {
    const pads = getGamepads();
    return pads[0] || pads[1] || pads[2] || pads[3];
}

// Help toggle handler
export function setupHelpToggle() {
    document.addEventListener('keydown', (event) => {
        if (event.key === 'h' || event.key === 'H') {
            const info = document.getElementById('info');
            if (info) {
                info.style.display = info.style.display === 'none' ? 'block' : 'none';
            }
        }
    });
}
